import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import DropdownContainer from 'components/Bookings2/List/Filter/DropdownContainer';
import filterStyles from 'components/Bookings2/List/Filter/Filter.module.scss';
import Footer from 'components/Bookings2/List/Filter/Footer';
import Header from 'components/Bookings2/List/Filter/Header';
import ListContent from 'components/Bookings2/List/Filter/ListContent';
import uniq from 'lodash/uniq';
import { FunctionComponent, useRef, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styles from './KanbanRoomUnitsFilter.module.scss';

export interface KanbanRoomUnitOption {
    id: number;
    name: string;
}

interface KanbanRoomUnitsFilterProps {
    units: Array<KanbanRoomUnitOption>;
    selectedUnitIds: Array<number>;
    onChange: (unitIds: Array<number>) => void;
}

export const KanbanRoomUnitsFilter: FunctionComponent<KanbanRoomUnitsFilterProps> = props => {
    const unitIds = uniq(props.units.map(unit => unit.id));
    const [expanded, setExpanded] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<Array<number>>(
        props.selectedUnitIds.length === 0 ? unitIds : props.selectedUnitIds
    );
    const [selectedAll, setSelectedAll] = useState(props.selectedUnitIds.length === 0);
    const selectedIdsRef = useRef(selectedIds);
    const selectedAllRef = useRef(selectedAll);
    const snapshotRef = useRef({ ids: selectedIds, all: selectedAll });
    const ignoreClickAwayRef = useRef(false);

    const allSelected = props.selectedUnitIds.length === 0;
    const label = allSelected
        ? 'All Units'
        : props.selectedUnitIds.length === 1
        ? props.units.find(unit => unit.id === props.selectedUnitIds[0])?.name ?? '1 unit'
        : `${props.selectedUnitIds.length} units`;

    const options = props.units.filter(unit => {
        if (searchText === '') {
            return true;
        }
        return unit.name.toLowerCase().includes(searchText.toLowerCase());
    });

    const commit = (ids: Array<number>, all: boolean) => {
        selectedIdsRef.current = ids;
        selectedAllRef.current = all;
        setSelectedIds(ids);
        setSelectedAll(all);
    };

    const restore = () => {
        commit(snapshotRef.current.ids, snapshotRef.current.all);
    };

    const open = () => {
        const ids = props.selectedUnitIds.length === 0 ? unitIds : [...props.selectedUnitIds];
        const all = props.selectedUnitIds.length === 0;
        snapshotRef.current = { ids, all };
        commit(ids, all);
        setSearchText('');
        setExpanded(true);
    };

    const close = () => {
        restore();
        setExpanded(false);
    };

    const apply = () => {
        ignoreClickAwayRef.current = true;
        const ids = [...selectedIdsRef.current];
        const all = unitIds.length > 0 && unitIds.every(id => ids.includes(id));
        snapshotRef.current = { ids: all ? unitIds : ids, all };
        commit(snapshotRef.current.ids, all);
        setExpanded(false);
        props.onChange(all ? [] : ids);
    };

    return (
        <div className={clsx(filterStyles.container, styles.root)}>
            <button
                type="button"
                className={filterStyles.plainTrigger}
                onClick={event => {
                    event.stopPropagation();
                    expanded ? close() : open();
                }}
            >
                <span>{label}</span>
                <FontAwesomeIcon icon={faAngleDown} />
            </button>
            {expanded && (
                <ClickAwayListener
                    onClickAway={() => {
                        if (ignoreClickAwayRef.current) {
                            ignoreClickAwayRef.current = false;
                            return;
                        }
                        close();
                    }}
                >
                    <div>
                        <DropdownContainer
                            className={styles.dropdown}
                            header={
                                <Header
                                    allLabel="All Units"
                                    selectedAll={selectedAll}
                                    onSearch={setSearchText}
                                    onSelectAll={selected => commit(selected ? [...unitIds] : [], selected)}
                                    onClose={close}
                                />
                            }
                            footer={
                                <Footer
                                    disabled={selectedIds.length === 0}
                                    onApply={apply}
                                />
                            }
                        >
                            <ListContent
                                options={options.map(unit => ({ id: unit.id, name: unit.name }))}
                                selectedIds={selectedIds}
                                onChange={ids => {
                                    const next = [...ids];
                                    const all = unitIds.length > 0 && unitIds.every(id => next.includes(id));
                                    commit(next, all);
                                }}
                            />
                        </DropdownContainer>
                    </div>
                </ClickAwayListener>
            )}
        </div>
    );
};

export default KanbanRoomUnitsFilter;
